import { Link, useMatchRoute } from '@tanstack/react-router';

import { savedViewsApi } from '@/api/queries/workspace';
import { clientRoleLabel } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import {
  currentMembership,
  currentTenantName,
  isSuperAdmin,
  isTenantAdmin,
  useTenantStore,
} from '@/auth/tenantStore';
import { Avatar, Icon, IconButton, initialsOf, type IconName } from '@/components/ui';
import { cn } from '@/utils/cn';
import { TenantSwitcher } from './TenantSwitcher';

interface NavEntry {
  to: string;
  label: string;
  icon: IconName;
  count?: number;
  search?: Record<string, unknown>;
  matchOptions?: Parameters<ReturnType<typeof useMatchRoute>>[0];
}

const NAV_ITEMS: NavEntry[] = [
  { to: '/contacts', label: 'Contacts', icon: 'users', search: { page: 0, pageSize: 25 } },
  { to: '/segments', label: 'Segments', icon: 'segments' },
  { to: '/counters', label: 'Counters', icon: 'database', search: { view: 'grid' } },
  { to: '/gdpr', label: 'GDPR', icon: 'shield', search: { tab: 'expiring' } },
  { to: '/audit-logs', label: 'Audit logs', icon: 'history', search: { page: 0, pageSize: 25 } },
];

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { data: savedViews = [] } = savedViewsApi.useList();
  const deleteView = savedViewsApi.useDelete();
  // Only user-scoped views are personal sidebar shortcuts; workspace-scoped
  // (shared) views surface as tabs on the contacts list instead.
  const localViews = savedViews.filter((v) => v.scope === 'user');
  const superAdmin = useTenantStore(isSuperAdmin);
  const membership = useTenantStore(currentMembership);
  const tenantName = useTenantStore(currentTenantName);
  const externalLinks = membership?.externalLinks ?? [];

  // Per-tenant branding: prefer the configured logo initials, otherwise derive a
  // 2-letter badge from the tenant name.
  const brandName = tenantName || 'Datahub';
  const brandInitials =
    membership?.logoInitials || (tenantName ? tenantName.slice(0, 2).toUpperCase() : 'DH');

  // Tenant admins (and super-admins) can manage the current tenant's team.
  const canManageTeam = useTenantStore(isTenantAdmin);

  return (
    <>
      {mobileOpen && <div className="app-sidebar-scrim" onClick={onClose} />}
      <aside className={cn('app-sidebar', mobileOpen && 'open')}>
        <div className="sidebar-brand">
          <div className="logo">{brandInitials}</div>
          <div>
            <div className="brand-name">{brandName}</div>
            <div className="brand-sub">Datahub</div>
          </div>
        </div>

        <div className="sidebar-scroll">
          <div className="sidebar-section sidebar-tenant">
            <TenantSwitcher />
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-label">Workspace</div>
            {NAV_ITEMS.map((entry) => (
              <SidebarNavLink key={entry.to} entry={entry} onNavigate={onClose} />
            ))}
          </div>

          {externalLinks.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-label">External</div>
              {externalLinks.map((link, i) => (
                <a
                  key={`${i}-${link.url}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-item"
                  title={link.url}
                  onClick={onClose}
                >
                  <Icon name="externalLink" className="nav-icon" />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {link.label}
                  </span>
                </a>
              ))}
            </div>
          )}

          {superAdmin && (
            <div className="sidebar-section">
              <div className="sidebar-section-label">Admin</div>
              <Link
                to="/admin/tenants"
                className="nav-item"
                activeProps={{ 'aria-current': 'page' }}
                onClick={onClose}
              >
                <Icon name="shield" className="nav-icon" />
                <span>Tenants</span>
              </Link>
              <Link
                to="/admin/jobs"
                search={{ tab: 'processing' }}
                className="nav-item"
                activeProps={{ 'aria-current': 'page' }}
                onClick={onClose}
              >
                <Icon name="clock" className="nav-icon" />
                <span>Jobs</span>
              </Link>
            </div>
          )}

          {localViews.length > 0 && (
            <>
              <div className="sidebar-section">
                <div className="sidebar-section-label">Saved views</div>
              </div>
              <div className="sidebar-views">
                {localViews.map((v) => (
                  <div key={v.id} className="view-item-wrap">
                    <Link
                      to="/contacts"
                      search={{
                        page: 0,
                        pageSize: 25,
                        ...(v.filters as Record<string, unknown>),
                      } as never}
                      className="view-item"
                      onClick={onClose}
                    >
                      <span className="view-dot" style={{ background: v.color ?? '#94A3B8' }} />
                      <span className="view-name">{v.name}</span>
                    </Link>
                    <IconButton
                      icon="trash"
                      iconSize={12}
                      size="sm"
                      label={`Delete view ${v.name}`}
                      className="view-item-del"
                      onClick={() => {
                        if (confirm(`Delete saved view "${v.name}"? This can't be undone.`)) {
                          deleteView.mutate(v.id);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="sidebar-section">
            <div className="sidebar-section-label">Settings</div>
            <Link
              to="/settings/api-tokens"
              className="nav-item"
              activeProps={{ 'aria-current': 'page' }}
              onClick={onClose}
            >
              <Icon name="settings" className="nav-icon" />
              <span>API tokens</span>
            </Link>
            <Link
              to="/settings/integrations"
              className="nav-item"
              activeProps={{ 'aria-current': 'page' }}
              onClick={onClose}
            >
              <Icon name="mail" className="nav-icon" />
              <span>Integrations</span>
            </Link>
            <Link
              to="/settings/linkedin-extension"
              className="nav-item"
              activeProps={{ 'aria-current': 'page' }}
              onClick={onClose}
            >
              <Icon name="link" className="nav-icon" />
              <span>LinkedIn extension</span>
            </Link>
            <Link
              to="/settings/custom-fields"
              className="nav-item"
              activeProps={{ 'aria-current': 'page' }}
              onClick={onClose}
            >
              <Icon name="database" className="nav-icon" />
              <span>Custom fields</span>
            </Link>
            {canManageTeam && (
              <Link
                to="/settings/team"
                className="nav-item"
                activeProps={{ 'aria-current': 'page' }}
                onClick={onClose}
              >
                <Icon name="users" className="nav-icon" />
                <span>Team</span>
              </Link>
            )}
            {canManageTeam && (
              <Link
                to="/settings/external-links"
                className="nav-item"
                activeProps={{ 'aria-current': 'page' }}
                onClick={onClose}
              >
                <Icon name="globe" className="nav-icon" />
                <span>External links</span>
              </Link>
            )}
            {canManageTeam && (
              <Link
                to="/settings/jobs"
                className="nav-item"
                activeProps={{ 'aria-current': 'page' }}
                onClick={onClose}
              >
                <Icon name="settings" className="nav-icon" />
                <span>Jobs</span>
              </Link>
            )}
          </div>
        </div>

        <SidebarFooter />
      </aside>
    </>
  );
}

function SidebarNavLink({ entry, onNavigate }: { entry: NavEntry; onNavigate: () => void }) {
  const matchRoute = useMatchRoute();
  // For routes that don't yet exist (segments/tags), `to` will be a stub link;
  // matchRoute returns false and the item just won't highlight — TanStack Router
  // will 404 on click, but the visual surface is correct for Phase 2.
  const active = !!matchRoute({ to: entry.to, fuzzy: true });

  return (
    <Link
      to={entry.to}
      search={entry.search as never}
      className="nav-item"
      activeProps={{ 'aria-current': 'page' }}
      onClick={onNavigate}
    >
      <Icon name={entry.icon} className="nav-icon" />
      <span>{entry.label}</span>
      {entry.count !== undefined && <span className="nav-count">{entry.count.toLocaleString()}</span>}
      {/* aria-current driven highlight is preferred; fall back to local match for visual parity */}
      {active && null}
    </Link>
  );
}

function SidebarFooter() {
  const { user, logout } = useAuth();
  const tenantName = useTenantStore(currentTenantName);
  const clientRole = useTenantStore((s) => s.me?.clientRole);

  const display =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.username ?? user?.email ?? 'Unknown user';

  const role = clientRoleLabel(clientRole);
  const subtitle = tenantName ? `${tenantName} · ${role}` : role;

  const initials = initialsOf(user?.firstName ?? user?.username, user?.lastName);

  return (
    <div className="sidebar-footer">
      <Avatar initials={initials} size={28} />
      <div className="user-meta">
        <div className="uname">{display}</div>
        <div className="urole">{subtitle}</div>
      </div>
      <IconButton icon="logout" iconSize={14} label="Sign out" onClick={() => void logout()} />
    </div>
  );
}
